<script setup lang="ts">
/**
 * Aura Incorporation — NOTICE & DECLARATION FOR NOMINEE SHAREHOLDER (Companies Act s.386ALB). 주주 1인당 1부(ns_{id}/).
 *   템플릿(Notice for Nominee Shareholder): YES/NO 체크 = [[if is_nominee_shareholder]],
 *   NAME OF NOMINEE SHAREHOLDER = Yes 면 [[nominee_shareholder_name]], No 면 “N.A.”(원문 기본값).
 *   nominee_shareholder_name 은 통지를 받는 주주 본인 — 생성 시 서명자로도 쓴다.
 */
import { computed, inject, onMounted } from 'vue';
import CddVarField from './cdd/CddVarField.vue';
import AuraNominatorParticulars from './AuraNominatorParticulars.vue';
import { useCddFill, DOC_SCOPE } from './cdd/useCddFill';
import './allotDoc.css';

const fill = useCddFill();
const scope = inject(DOC_SCOPE, '');
const K = (k: string) => scope + k;

const isNominee = computed(() => String(fill.values[K('is_nominee_shareholder')] ?? '').toLowerCase() === 'yes');
function setNominee(v: boolean) {
  fill.setValue(K('is_nominee_shareholder'), v ? 'yes' : 'no');
}
onMounted(() => {
  if (!String(fill.values[K('is_nominee_shareholder')] ?? '').trim()) fill.setValue(K('is_nominee_shareholder'), 'no');
});
</script>

<template>
  <div class="allot-doc">
    <div class="doc-lbl-title" style="text-align:center">NOTICE &amp; DECLARATION FOR NOMINEE SHAREHOLDER</div>
    <p class="doc-p"><b>Company Name:</b> <CddVarField field-key="company_name" label="Company Name" /></p>
    <p class="doc-p"><b>Registration No. (UEN):</b> <CddVarField field-key="company_registration_no" label="Registration No. (UEN)" /></p>

    <p class="doc-p">
      This notice is sent under Section 386ALB of the Companies Act 1967 of Singapore. Please complete and declare the
      following information; failure to do so as required by this notice may be an offence.
    </p>

    <table class="cdd-tbl ns-box">
      <tbody>
        <tr>
          <td>
            <b>WHAT IS A NOMINEE SHAREHOLDER?</b><br />
            A shareholder is a nominee if the shareholder is accustomed or under an obligation (whether formal or informal)
            to vote, in respect of shares in the company of which the shareholder is the registered holder, in accordance
            with the directions, instructions or wishes of any other person; and receives dividends, in respect of shares in
            the company of which the shareholder is the registered holder, on behalf of any other person.
          </td>
        </tr>
      </tbody>
    </table>

    <p class="doc-p"><b>Are you acting as a Nominee Shareholder of the Company?</b></p>
    <p class="doc-p">
      <span class="cdd-cbx" @click="setNominee(true)"><span class="box">{{ isNominee ? '✓' : '' }}</span> YES</span>
      <span class="cdd-cbx" @click="setNominee(false)"><span class="box">{{ !isNominee ? '✓' : '' }}</span> NO</span>
      (PLEASE TICK ONE)
    </p>
    <p class="doc-p">
      If your reply is YES, please provide your full name and the particulars of the nominator(s). If your reply is NO,
      kindly indicate “N.A.” in the field for the name of the Nominee Shareholder.
    </p>
    <table class="cdd-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell" style="width:46%">• NAME OF NOMINEE SHAREHOLDER:</td>
          <td>
            <template v-if="isNominee"><CddVarField field-key="nominee_shareholder_name" label="Shareholder Name" /></template>
            <b v-else>N.A.</b>
          </td>
        </tr>
      </tbody>
    </table>
    <!-- No 일 때도 서명 주체(주주) 이름은 받아 둔다 — 문서엔 N.A., T-Sign 서명자로 사용 -->
    <p v-if="!isNominee" class="doc-p ns-signer">
      Shareholder (signs this notice): <CddVarField field-key="nominee_shareholder_name" label="Shareholder Name" />
    </p>

    <AuraNominatorParticulars :active="isNominee" />

    <p class="doc-p" style="margin-top:12px">
      I hereby declare that the information provided in this notice is true and accurate to the best of my knowledge. I
      undertake to notify the Company within 30 days if I (become/cease to be) a nominee shareholder of the Company, or if
      there are any changes to the particulars provided herein.
    </p>
    <p class="doc-p">Dated: <CddVarField field-key="dated_on" label="Dated" /></p>
    <p class="doc-p">Yours sincerely,</p>
    <div class="sig-line">____________________________________</div>
  </div>
</template>

<style scoped>
.ns-box td { font-size: 12px; line-height: 1.5; }
.ns-signer { font-size: 12px; color: #374151; }
.sig-line { margin-top: 26px; }
</style>
